import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Crown, TrendingDown, ExternalLink } from 'lucide-react';
import { Currency, ComparisonResult, Country } from '../types/calculator';
import { calculateFees, PROVIDERS, getProviderFees, formatXOF } from '../utils/feeCalculator';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { openAppStore } from '../utils/deepLink';
interface ComparisonResultsProps {
  amount: number;
  currency: Currency;
}
export function ComparisonResults({
  amount,
  currency
}: ComparisonResultsProps) {
  const {
    language
  } = useLanguage();
  const t = translations[language].results;
  const [selectedCountry, setSelectedCountry] = useState<Country>('ALL');
  const results: ComparisonResult[] = Object.values(PROVIDERS).map(provider => {
    const breakdown = calculateFees(amount, currency, provider.id);
    return {
      provider,
      breakdown,
      isCheapest: false
    };
  });
  // Filter by country
  const filteredResults = results.filter(result => {
    if (selectedCountry === 'ALL') return true;
    // Show providers that match the selected country OR have 'ALL' as their country
    return result.provider.country === selectedCountry || result.provider.country === 'ALL';
  });
  // Get current provider fees (dynamic from Supabase or defaults)
  const PROVIDER_FEES = getProviderFees();
  // Calculate total charge for each provider and sort by it
  const resultsWithCharge = filteredResults.map(result => {
    const fee = PROVIDER_FEES[result.provider.id];
    const totalCharge = fee === 'not_defined' ? Infinity : result.breakdown.totalCost + (fee as number);
    return {
      ...result,
      totalCharge
    };
  });
  // Sort by total charge (lowest first, undefined fees last)
  resultsWithCharge.sort((a, b) => a.totalCharge - b.totalCharge);
  // Mark the cheapest (first one with defined fee)
  const cheapestIndex = resultsWithCharge.findIndex(r => r.totalCharge !== Infinity);
  if (cheapestIndex !== -1) {
    resultsWithCharge[cheapestIndex].isCheapest = true;
  }
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div variants={container} initial="hidden" animate="show" className="mt-8" data-id="element-23">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-24">
        <div className="flex items-center gap-2" data-id="element-25">
          <TrendingDown className="w-5 h-5 text-emerald-600" data-id="element-26" />
          <h3 className="text-xl font-bold text-gray-900" data-id="element-27">{t.title}</h3>
        </div>

        {/* Country Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-id="element-28">
          <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-29">
            {t.filterByCountry}:
          </span>
          <div className="flex gap-2 flex-wrap" data-id="element-30">
            <button onClick={() => setSelectedCountry('ALL')} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedCountry === 'ALL' ? 'bg-emerald-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-31">
              {t.allCountries}
            </button>
            <button onClick={() => setSelectedCountry('SN')} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedCountry === 'SN' ? 'bg-emerald-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-32">
              🇸🇳 {t.senegal}
            </button>
            <button onClick={() => setSelectedCountry('CI')} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedCountry === 'CI' ? 'bg-emerald-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-33">
              🇨🇮 {t.coteDivoire}
            </button>
          </div>
        </div>
      </div>

      {/* Update Notice Badge */}
      <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium" data-id="element-34">
        {t.updateNotice}
      </div>

      <div className="space-y-4" data-id="element-35">
        {resultsWithCharge.map((result, index) => {
        const fee = PROVIDER_FEES[result.provider.id];
        const feeDisplay = fee === 'not_defined' ? t.feeNotSpecified : formatXOF(fee);
        const totalChargeDisplay = result.totalCharge === Infinity ? t.feeNotSpecified : formatXOF(result.totalCharge);
        // Calculate savings compared to cheapest option
        const cheapestCharge = resultsWithCharge[cheapestIndex]?.totalCharge;
        const savingsAmount = result.totalCharge === Infinity || cheapestCharge === Infinity ? null : result.totalCharge - cheapestCharge;
        return <motion.div key={result.provider.id} variants={item} className={`relative bg-white rounded-xl p-6 border-2 transition-all ${result.isCheapest ? 'border-emerald-500 shadow-lg' : 'border-gray-200 shadow-sm'}`} data-id="element-36">
              {result.isCheapest && <div className="absolute -top-3 left-6 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-37">
                  <Crown className="w-3 h-3" data-id="element-38" />
                  {t.bestOption}
                </div>}

              <div className="flex items-start justify-between mb-4" data-id="element-39">
                <div className="flex items-center gap-3" data-id="element-40">
                  <div className={`w-12 h-12 ${result.provider.bgColor} rounded-lg flex items-center justify-center overflow-hidden`} data-id="element-41">
                    {result.provider.logoUrl ? <img src={result.provider.logoUrl} alt={result.provider.name} className="w-8 h-8 object-contain" data-id="element-42" /> : <span className={`text-xl font-bold ${result.provider.color}`} data-id="element-43">
                        {result.provider.name.charAt(0)}
                      </span>}
                  </div>
                  <div data-id="element-44">
                    <h4 className="text-lg font-bold text-gray-900" data-id="element-45">
                      {result.provider.name}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-46">
                      #{index + 1} {t.mostAffordable}
                    </p>
                  </div>
                </div>

                <div className="text-right" data-id="element-47">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-48">
                    {totalChargeDisplay}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-49">{t.totalCharge}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-100" data-id="element-50">
                <div data-id="element-51">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-52">
                    {t.conversionRate}
                  </div>
                  <div className="text-sm font-semibold text-gray-900" data-id="element-53">
                    {formatXOF(result.breakdown.totalCost)}
                  </div>
                </div>
                <div data-id="element-54">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-55">{t.fee}</div>
                  <div className="text-sm font-semibold text-gray-900" data-id="element-56">
                    {feeDisplay}
                  </div>
                </div>
                <div data-id="element-57">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-58">{t.fxRate}</div>
                  <div className="text-sm font-semibold text-gray-900" data-id="element-59">
                    {currency === 'XOF' ? '1:1' : `${result.breakdown.displayedFxRate} XOF`}
                  </div>
                </div>
                <div data-id="element-60">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-61">{t.savings}</div>
                  <div className={`text-sm font-semibold ${result.isCheapest || savingsAmount === null ? 'text-gray-900' : 'text-red-600'}`} data-id="element-62">
                    {result.isCheapest || savingsAmount === null ? '—' : `+${formatXOF(savingsAmount)}`}
                  </div>
                </div>
              </div>

              {/* Open in App Button */}
              {result.provider.appUrl && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-63">
                  <button onClick={() => openAppStore(result.provider.appUrl!, result.provider.deepLinkScheme)} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${result.isCheapest ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md hover:shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-64">
                    <ExternalLink className="w-4 h-4" data-id="element-65" />
                    {t.openInApp}
                  </button>
                </div>}
            </motion.div>;
      })}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-66">
        <p className="text-sm text-blue-900" data-id="element-67">
          <strong data-id="element-68">Note:</strong> {t.note}
        </p>
      </div>
    </motion.div>;
}